import React from 'react';
import { getResidents } from '@/api/residents';
import { Box, Button, Container, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus';
import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react/dist/ssr/UsersThree';
import { useQuery } from '@tanstack/react-query';
import { useDialog } from '@/hooks/v2/use-dialog';

import type { User } from '@/types/v2/user';
import { useUser } from '@/hooks/v2/use-user';
import { Seo } from '@/components/seo';

import { ResidentsTable } from './components/residents-table';
import CreateResidentDialog from './components/create-resident-dialog';

export function ResidentsPage(): React.JSX.Element {
  const { community } = useUser();
  const dialog = useDialog();

  const { data, isLoading, error, refetch } = useQuery<{ residents: User[] }>({
    queryKey: ['residents', community?.id],
    queryFn: async () => {
      const response = await getResidents(community?.id, {
        include: 'units,communityRoles',
        all: true,
      });
      return response;
    },
    retry: false,
  });

  const residents = data?.residents || [];

  const handleCreated = React.useCallback(() => {
    refetch();
    dialog.handleClose();
  }, [refetch, dialog]);

  if (!community?.id) {
    return (
      <>
        <Seo title="Users" />
        <Box component="main" sx={{ flexGrow: 1, py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body1">
            No community selected
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title="Users" />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Users</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                  Manage residents, view unit assignments, and community membership
                </Typography>
              </Box>
              <Button
                onClick={dialog.handleOpen}
                size="small"
                startIcon={<PlusIcon size={16} />}
                variant="contained"
              >
                Add User
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            {isLoading ? (
              <Stack>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Box key={i} sx={{ px: 3, py: 2 }}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton height={18} variant="text" width="55%" />
                        <Skeleton height={14} sx={{ mt: 0.5 }} variant="text" width="35%" />
                      </Box>
                      <Skeleton height={14} variant="text" width={80} />
                    </Stack>
                    {i < 5 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Stack>
            ) : error ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="error" variant="body2">
                  Unable to load users. Please try again.
                </Typography>
              </Box>
            ) : residents.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <UsersThreeIcon color="var(--mui-palette-text-disabled)" size={48} style={{ marginBottom: 12 }} />
                <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body1">
                  No users yet
                </Typography>
                <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="body2">
                  Add a user to get started
                </Typography>
              </Box>
            ) : (
              <ResidentsTable
                communityId={community.id}
                loading={isLoading}
                onCreated={() => refetch()}
                rows={residents}
              />
            )}
          </Paper>
        </Container>
      </Box>

      <CreateResidentDialog
        communityId={community.id}
        onClose={dialog.handleClose}
        onCreated={handleCreated}
        open={dialog.open}
      />
    </>
  );
}
